const HTTP_TIMEOUT_MS = 10_000;

export class HttpTimeoutError extends Error {
  readonly endpoint: string;

  constructor(endpoint: string) {
    super(`Request timed out after ${HTTP_TIMEOUT_MS / 1000}s`);
    this.name = "HttpTimeoutError";
    this.endpoint = endpoint;
  }
}

type TimeoutListener = (endpoint: string) => void;

const timeoutListeners = new Set<TimeoutListener>();

export function subscribeHttpTimeout(listener: TimeoutListener): () => void {
  timeoutListeners.add(listener);
  return () => {
    timeoutListeners.delete(listener);
  };
}

function notifyHttpTimeout(endpoint: string): void {
  timeoutListeners.forEach((listener: TimeoutListener) => {
    listener(endpoint);
  });
}

function resolveUrlString(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  return input.url;
}

function linkAbortSignals(
  primary: AbortController,
  external: AbortSignal | null | undefined,
): void {
  if (external == null) {
    return;
  }
  if (external.aborted) {
    primary.abort();
    return;
  }
  external.addEventListener("abort", () => primary.abort(), { once: true });
}

function isAbortError(err: unknown): boolean {
  return (
    err instanceof DOMException && err.name === "AbortError"
  ) || (err instanceof Error && err.name === "AbortError");
}

/**
 * `fetch` with a 10s timeout. On timeout, logs the endpoint to the console,
 * notifies subscribers (for a global error modal), and throws {@link HttpTimeoutError}.
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const endpoint = resolveUrlString(input);
  const controller = new AbortController();
  linkAbortSignals(controller, init?.signal);

  let didTimeout = false;
  const timeoutId = window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, HTTP_TIMEOUT_MS);

  try {
    const response = await fetch(input, {
      ...init,
      signal: controller.signal,
    });
    window.clearTimeout(timeoutId);
    return response;
  } catch (err) {
    window.clearTimeout(timeoutId);
    if (isAbortError(err) && didTimeout) {
      console.error(
        "[HTTP] Request timed out (10s). Failed endpoint:",
        endpoint,
      );
      notifyHttpTimeout(endpoint);
      throw new HttpTimeoutError(endpoint);
    }
    throw err;
  }
}
